import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { Search, Users, MapPin, Loader2, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

interface Team {
  team_id: number;
  team_name: string;
  sport_type: string | null;
  location: string | null;
  city: string | null;
  state_province: string | null;
  team_logo: string | null;
  created_by: string | null;
}

interface TeamConnectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  onTeamConnected: () => void;
}

export const TeamConnectionDialog: React.FC<TeamConnectionDialogProps> = ({
  open,
  onOpenChange,
  userId,
  onTeamConnected,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      fetchTeams();
    }
  }, [open, searchQuery]);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('teams')
        .select('team_id, team_name, sport_type, location, city, state_province, team_logo, created_by')
        .order('team_name');

      if (searchQuery.trim()) {
        query = query.ilike('team_name', `%${searchQuery}%`);
      }

      const { data, error } = await query.limit(50);

      if (error) {
        console.error('Error fetching teams:', error);
        return;
      }

      setTeams(data || []);
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectTeam = async (team: Team) => {
    setConnecting(team.team_id);
    try {
      // Fetch current connected_teams from user profile
      const { data: profileData, error: profileFetchError } = await supabase
        .from('internal_user_profiles')
        .select('connected_teams')
        .eq('user_id', userId)
        .single();

      if (profileFetchError) {
        console.error('Error fetching profile:', profileFetchError);
        toast.error('Failed to connect team. Please try again.');
        return;
      }

      const currentTeams = (profileData?.connected_teams as number[]) || [];
      
      // Check if already connected
      if (currentTeams.includes(team.team_id)) {
        toast.info(`You're already connected to ${team.team_name}`);
        onOpenChange(false);
        return;
      }

      // Add team to connected_teams array
      const { error: updateError } = await supabase
        .from('internal_user_profiles')
        .update({ 
          connected_teams: [...currentTeams, team.team_id] 
        })
        .eq('user_id', userId);

      if (updateError) {
        console.error('Error updating connected teams:', updateError);
        toast.error('Failed to connect team. Please try again.');
        return;
      }

      toast.success(`Successfully connected to ${team.team_name}!`);
      onTeamConnected();
      onOpenChange(false);
    } catch (error) {
      console.error('Error connecting team:', error);
      toast.error('Failed to connect team. Please try again.');
    } finally {
      setConnecting(null);
    }
  };

  const getTeamLocation = (team: Team) => {
    const parts = [team.city, team.state_province, team.location].filter(Boolean);
    return parts.join(', ') || 'Location not specified';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5 text-sports-green" />
            Connect to Existing Team
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Search for your team and claim ownership to manage it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search teams by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-700 border-gray-600 text-white"
            />
          </div>

          <ScrollArea className="h-[300px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : teams.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No available teams found.</p>
                <p className="text-sm mt-1">Try a different search or create a new team.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {teams.map((team) => (
                  <div
                    key={team.team_id}
                    className="flex items-center justify-between p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center overflow-hidden">
                        {team.team_logo ? (
                          <img
                            src={team.team_logo}
                            alt={team.team_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Users className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white">{team.team_name}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          {team.sport_type && (
                            <span className="bg-gray-600 px-2 py-0.5 rounded text-xs">
                              {team.sport_type}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {getTeamLocation(team)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleConnectTeam(team)}
                      disabled={connecting === team.team_id}
                      className="bg-sports-green text-black hover:bg-green-600"
                    >
                      {connecting === team.team_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Connect'
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};
